import is from '@sindresorhus/is';
import { first } from 'lodash';
import type { FunctionComponent } from 'react';
import { useContext, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useHasRole } from '~/shared/context/use-has-role';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import type { JobItemDetails } from '../api/use-job-item-details';
import { useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { getJobTypeTranslation } from '../hooks/use-review-action';
import {
    getNewerVersions,
    getPublishedVersionInfo,
    hasReviewedOrPublishedVersion,
} from './notification-bar/notification-version-utils';
import { ArchivedPrompt, AssignReviewerPrompt, GeneratingPrompt, PublishPrompt, RoleReviewPrompt } from './prompts';

type ItemType = 'job-level' | 'job-role';

type JobProfileReviewPromptProps = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly version?: number;
    readonly preferredLanguage?: LanguageCodeForJSA;
};

type PromptKind = 'generating' | 'archived' | 'assign-reviewer' | 'role-review' | 'publish' | null;

const resolvePromptKind = (item: JobItemDetails | undefined, isJobArchitect: boolean): PromptKind => {
    if (item == null) return null;
    if (item.profileGenerationInfo?.inProgress === true) return 'generating';
    if (item.isArchived) return 'archived';
    if (item.versionInfo == null || item.versionInfo.isPublished) return null;
    if (item.versionInfo.isReviewed) {
        return item.actions?.canPublish === true && isJobArchitect ? 'publish' : null;
    }
    if (item.actions?.canAssignReviewer !== true) return null;
    return item.reviewer === undefined ? 'assign-reviewer' : 'role-review';
};

// Hidden when a newer version is reviewed/published (same guard as notification bars).
export const JobProfileReviewPrompt: FunctionComponent<JobProfileReviewPromptProps> = ({
    architectureId,
    itemId,
    itemType,
    version,
}) => {
    const { translate } = useContext(ApplicationContext);
    const isJobArchitect = useHasRole('job_architect');

    const { data: versions, isLoading: isLoadingVersions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const activeVersion = is.number(version) ? version.toString() : first(versions)?.version.toString();

    const { data: item } = useJobItemDetails({
        architectureId,
        enabled: !isLoadingVersions,
        itemId,
        itemType,
        version: activeVersion,
    });

    const isStaleVersion = useMemo(() => {
        const newerVersions = getNewerVersions(versions, item?.versionInfo?.version);
        return hasReviewedOrPublishedVersion(newerVersions);
    }, [versions, item?.versionInfo?.version]);

    const isNewVersion = useMemo(() => getPublishedVersionInfo(versions).hasPreviousPublishedVersion, [versions]);

    const kind = useMemo<PromptKind>(() => resolvePromptKind(item, isJobArchitect), [item, isJobArchitect]);

    if (item == null || kind == null || isStaleVersion) {
        return null;
    }

    const jobType = getJobTypeTranslation(itemType, translate);
    const title = item.title;

    switch (kind) {
        case 'generating':
            return <GeneratingPrompt jobType={jobType} />;
        case 'archived':
            return <ArchivedPrompt architectureId={architectureId} item={item} itemType={itemType} jobType={jobType} />;
        case 'assign-reviewer':
            return (
                <AssignReviewerPrompt
                    architectureId={architectureId}
                    item={item}
                    itemType={itemType}
                    jobType={jobType}
                />
            );
        case 'role-review':
            return (
                <RoleReviewPrompt
                    architectureId={architectureId}
                    isNewVersion={isNewVersion}
                    item={item}
                    itemType={itemType}
                    title={title}
                />
            );
        case 'publish':
            return (
                <PublishPrompt
                    architectureId={architectureId}
                    isNewVersion={isNewVersion}
                    item={item}
                    itemType={itemType}
                    title={title}
                />
            );
    }
};
